import { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { IListingRequest } from '@vivli/features/listing-request/infrastructure/interface';
import { ListingRequestStatusEnum } from '@vivli/features/listing-request/infrastructure/enum';
import {
    useActiveUserContext,
    useListingRequestApiContext,
    useModalApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { ListingRequestContext } from '@vivli/features/listing-request/infrastructure/context';
import { getStudyAlreadyExistsMessage } from '@vivli/features/listing-request/infrastructure/hook';
import { first } from 'rxjs/operators';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';
import { useNavigate, useParams } from 'react-router';
import { Subject } from 'rxjs';
import { StudyStatusEnum } from '@vivli/features/studies/infrastructure/enum';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { checkResearchTeamForDuplicates, usePathEndingHook, useFormDirtyTracking } from '@vivli/shared/infrastructure/hook';
import { IStudyResearchTeamMember } from '@vivli/features/studies/infrastructure/interface';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useFormContext } from 'react-hook-form';

interface ListingRequestContextWrapperProps {
    children: ReactNode;
    listingRequest?: IListingRequest;
    setListingRequest: Dispatch<SetStateAction<IListingRequest>>;
    loadingRequest: boolean;
}

export const ListingRequestContextWrapper = ({
    children,
    listingRequest,
    setListingRequest,
    loadingRequest
}: ListingRequestContextWrapperProps) => {
    const { id } = useParams();
    const tab = usePathEndingHook();
    const navigate = useNavigate();
    const { hasPermission } = useActiveUserContext();
    const { isDatasetOwner } = useUserPermissions();
    const listingRequestService = useListingRequestApiContext();
    const toastService = useToastApiContext();
    const modalService = useModalApiContext();
    const isSavingRef = useRef(false);
    const formApi = useFormContext<IListingRequest>();
    const isFormDirty = useFormDirtyTracking(formApi);

    const isVivliAdmin = hasPermission(PermissionsEnum.SubmissionsActAdv);
    const [isReadOnly, setIsReadOnly] = useState(isVivliAdmin); //TODO PERMFE - redo with permissions+
    const [isLoading, setIsLoading] = useState(false); //use for actions buttons take
    const [isEditable, setIsEditable] = useState(false);
    const urlPathName = location.pathname.split('/')[3] === 'StatusUpdate';

    const handleNext = (parsedTitle) => {
        let nextUrl = id ? `/study-submission/${id}/${parsedTitle}` : `${parsedTitle}`;
        if (!isReadOnly) {
            handleFormSave(false, null, (newId) => {
                nextUrl = newId ? `/study-submission/${newId}/${parsedTitle}` : `${parsedTitle}`;
                navigate(nextUrl, { replace: true });
                setIsReadOnly(false);
            });
            return;
        }

        navigate(nextUrl, { replace: true });
    };

    const updateIsSaving = (status: boolean) => {
        isSavingRef.current = status;
        setIsLoading(status);
    };

    const handleSavingError = (err: string) => {
        updateIsSaving(false);
        //a duplicate NCT ID or Sponsor Protocol ID is a validation failure, not an operational error,
        //so show the server's message on its own
        const duplicateStudyMessage = getStudyAlreadyExistsMessage(err);
        modalService.error(
            duplicateStudyMessage ?? `An error occurred with your operation. Please try again or contact Vivli support: ${err}`
        );
    };

    const handleSaveSuccess = (lr: IListingRequest) => {
        setListingRequest(lr);
        updateIsSaving(false);

        formApi.reset(lr, {
            keepErrors: true,
            keepIsValid: false,
            keepDirty: false
        });

        toastService.success('Edits saved successfully');
    };

    const clearStudyFromListingRequest = (updatedListingRequest: IListingRequest) => {
        const dataset = updatedListingRequest.datasets[0];
        const clearedStudy = {
            id: dataset.id,
            researchTeam: dataset.researchTeam,
            orgId: dataset.orgId,
            orgName: dataset.orgName,
            orgCode: dataset.orgCode,
            nctId: dataset.nctId,
            additionalInformation: dataset.additionalInformation,
            funder: dataset.funder,
            funderEmail: dataset.funderEmail,
            grant: dataset.grant,
            embargoDate: dataset.embargoDate,
            fundingOrganizations: dataset.fundingOrganizations
        };
        updatedListingRequest.datasets[0] = clearedStudy;
        return updatedListingRequest;
    };

    const handleFormSave = (isSubmit = false, nextStatus?: ListingRequestStatusEnum, nextEvent?: (id) => void) => {
        if (isVivliAdmin) {
            //TODO PERMFE - redo with permissions+
            setIsReadOnly(!urlPathName);
        }

        if (isSavingRef.current) {
            return;
        }

        const subject = new Subject<IListingRequest>();
        formApi.handleSubmit(
            (formValues) => handleFormApiSave(formValues, isSubmit, subject, nextStatus, nextEvent),
            () => {
                const formValues = formApi.getValues();
                handleFormApiSave(formValues, isSubmit, subject, nextStatus, nextEvent);
            }
        )();
        return subject;
    };

    function checkResearchTeamForDuplicatesLocal(teamMembers: IStudyResearchTeamMember[]) {
        const message = checkResearchTeamForDuplicates(teamMembers);
        if (message !== '') {
            modalService.error(message);
            return false;
        }
        return true;
    }

    const handleFormApiSave = (
        formValues: IListingRequest,
        isSubmit: boolean,
        subject: Subject<IListingRequest>,
        nextStatus?: ListingRequestStatusEnum,
        nextEvent?: (id?) => void
    ) => {
        let updatedListingRequest: IListingRequest = { ...formValues };

        if (isSubmit && !checkResearchTeamForDuplicatesLocal(updatedListingRequest.datasets[0].researchTeam)) {
            //save, but not submit
            handleFormApiSave(formValues, false, subject);
            return;
        }
        if (isFormDirty) {
            updateIsSaving(true);
            if (updatedListingRequest?.datasets[0]?.nctId === '') {
                if (updatedListingRequest?.datasets[0]?.sponsorProtocolId === '' && !updatedListingRequest?.datasets[0]?.isListedOnCtgov) {
                    updatedListingRequest = clearStudyFromListingRequest(updatedListingRequest);
                } else {
                    //can proceed because we are allowed to have a dataset
                    //with no nct id but a sponsor protocol id
                }
            }

            if (!updatedListingRequest.status) {
                // handle form api not passing in the default status (causes parse error)
                updatedListingRequest = {
                    ...updatedListingRequest,
                    status: ListingRequestStatusEnum.Draft
                };
            }

            listingRequestService
                .upsertListingRequest(updatedListingRequest)
                .pipe(first())
                .subscribe({
                    next: (resultListingRequest) => {
                        subject.next(resultListingRequest);
                        handleSaveSuccess(resultListingRequest);
                        if (isSubmit) {
                            handleStatusUpdate(resultListingRequest, nextStatus);
                        }
                        nextEvent && nextEvent(resultListingRequest?.id);
                    },
                    error: handleSavingError
                });
        } else {
            subject.next(listingRequest);
            if (isSubmit) {
                handleStatusUpdate(listingRequest, nextStatus);
            }
            nextEvent && nextEvent(listingRequest?.id);
        }
    };

    const handleStatusUpdate = (updatedListingRequest: IListingRequest, status: ListingRequestStatusEnum) => {
        //If listingRequest is moving to an Approved status, study status must be precheck or posted already
        const studyIsCuratedOrPosted =
            updatedListingRequest.datasets[0].status === StudyStatusEnum.SubmittedToVivliForPreCheck ||
            updatedListingRequest.datasets[0].status === StudyStatusEnum.Posted;
        if (status === ListingRequestStatusEnum.Approved && !studyIsCuratedOrPosted) {
            modalService.message('The study must be in Awaiting Vivli Review or Posted Status before approving this request.', {
                showBackground: false,
                showLogo: true,
                showContinueButton: true
            });
            return;
        }

        updateIsSaving(true);
        listingRequestService
            .updateListingRequestStatus(updatedListingRequest, status)
            .pipe(first())
            .subscribe((resultListingRequest) => {
                setListingRequest(resultListingRequest);
                updateIsSaving(false);
                //Return to grid
                const returnTab = getReturnTab(status);
                //TODO PERMFE - redo with permissions+
                const returnUrl =
                    isDatasetOwner || isVivliAdmin ? `/admin/study-submissions/${returnTab}` : `/study-submissions/${returnTab}`;
                navigate(returnUrl);
            }, handleSavingError);
    };

    const getReturnTab = (statusString) => {
        switch (statusString) {
            case 'Submitted':
                return 'inprogress';
            case 'Study In Curation':
                return 'inprogress';
            case 'Approved':
                return 'approved';
            case 'Withdrawn':
                return 'withdrawn';
            default:
                return 'draft';
        }
    };

    const provider = {
        listingRequest,
        setListingRequest,
        isLoading,
        isFormDirty,
        handleFormSave,
        handleStatusUpdate,
        isReadOnly,
        setIsReadOnly,
        handleNext,
        isEditable,
        setIsEditable
    };

    useEffect(() => {
        if (!listingRequest) {
            return;
        }

        if (listingRequest.id !== undefined && id === undefined) {
            navigate(`${listingRequest.id}/${tab}`, { replace: true });
        }
        //TODO PERMFE - redo with permissions+
        const userReadOnly = listingRequest?.status !== ListingRequestStatusEnum.Draft && !isVivliAdmin;
        const readOnly = userReadOnly || isVivliAdmin;
        setIsReadOnly(urlPathName ? false : readOnly);
    }, [listingRequest]);

    if (loadingRequest) {
        return <LoadIndicatorCenteredView />;
    }

    return <ListingRequestContext.Provider value={provider}>{children}</ListingRequestContext.Provider>;
};
